import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { AlertTriangle } from 'lucide-react';
import TextFieldRenderer from './FieldRenderers/TextFieldRenderer';
import TextareaRenderer from './FieldRenderers/TextareaRenderer';
import DropdownRenderer from './FieldRenderers/DropdownRenderer';
import CheckboxRenderer from './FieldRenderers/CheckboxRenderer';
import DateRenderer from './FieldRenderers/DateRenderer';
import TableRenderer from './FieldRenderers/TableRenderer';
import ObjectiveProgressRenderer from './FieldRenderers/ObjectiveProgressRenderer';
import MSEFieldGroup from './FieldRenderers/MSEFieldGroup';
import RiskAssessmentGroup from './FieldRenderers/RiskAssessmentGroup';
import SafetyPlanGroup from './FieldRenderers/SafetyPlanGroup';
import VitalsFieldGroup from './FieldRenderers/VitalsFieldGroup';
import DiagnosisAutocomplete from './FieldRenderers/DiagnosisAutocomplete';
import MedicationAutocomplete from './FieldRenderers/MedicationAutocomplete';
import { shouldShowField as shouldShowFieldUtil, validateForm as validateFormUtil } from './dynamicFormUtils';
import { useChartData } from './useChartData';
import { logger } from "../../utils/logger";
import { resolveSchemaFields } from './schemaCompat';
import { GroundingReviewAlert, formatFieldLabel, getFlaggedFieldKeys } from '../shared/GroundingReviewAlert';
import PrescriptionActionLauncher from '../../features/my-settings/PrescriptionActionLauncher';
import { get } from '../../utils/apiClient';
import { formatMedicationsAsText, transformMedicationsForForm } from '../../features/notes/utils/chartDataHelpers';

// Resolve the canonical key for a template field definition.
// Prefers field_id (standard) but falls back to name for legacy templates.
const getFieldKey = (field) => field.field_id || field.name;

// Child ids that mark a generic `group` as a mental status exam group.
const MSE_GROUP_FIELD_IDS = new Set([
  'orientation',
  'post_orientation',
  'post_coherence',
  'post_emotional_state',
  'speech',
  'mood',
  'affect',
  'thought_process',
  'thought_content',
  'perceptual_disturbances',
  'cognition',
  'insight',
  'judgment',
  'motor_activity',
  'interview_behavior',
]);

const DynamicForm = ({ template, initialData = {}, onChange, onSubmit, readOnly = false, isNoteOwner = true, createdByName = null, fieldAttribution = null, patientId, practitionerId, caseId = null, noteId = undefined, encounterId = undefined, canPrescribe = false, noEncounter = false, groundingReview = null, highlightMissingFields = false, reportedInformation = null, allowChartPrefill = true }) => {
  void noEncounter;
  const [formData, setFormData] = useState(initialData);
  const formDataRef = useRef(initialData);
  const [errors, setErrors] = useState({});
  const effectiveReadOnly = readOnly;

  // Resolve fields from template schema, with fallback for legacy "sections" format
  const fields = React.useMemo(
    () => resolveSchemaFields(template.template_schema),
    [template.template_schema],
  );
  // Build a map of field key -> field definition for quick lookup
  const fieldDefinitionMap = React.useMemo(() => {
    const map = {};
    const processFields = (fieldList) => {
      if (!fieldList) return;
      for (const field of fieldList) {
        const key = getFieldKey(field);
        if (key) {
          map[key] = field;
        }
        // Process nested fields in groups
        if (field.fields) {
          processFields(field.fields);
        }
      }
    };
    processFields(fields);
    return map;
  }, [fields]);

  // Helper to check if a field is editable based on note ownership and template's staff_entry_allowed
  // If user is note owner, they can edit all fields
  // If user is not note owner but has view access, they can only edit fields with staff_entry_allowed: true
  const isFieldEditable = useCallback((fieldKey) => {
    if (effectiveReadOnly) return false;
    if (isNoteOwner) return true; // Note owner can edit all fields

    // Non-owner can only edit fields with staff_entry_allowed: true
    const fieldDef = fieldDefinitionMap[fieldKey];
    return fieldDef?.staff_entry_allowed === true;
  }, [effectiveReadOnly, isNoteOwner, fieldDefinitionMap]);

  // Build a normalized set of flagged field keys/labels from the AI grounding review.
  const flaggedFieldSet = React.useMemo(() => {
    const keys = getFlaggedFieldKeys(groundingReview);
    const set = new Set<string>();
    keys.forEach((key) => {
      set.add(key.toLowerCase());
      set.add(formatFieldLabel(key).toLowerCase());
    });
    return set;
  }, [groundingReview]);

  const isFieldFlagged = (field) => {
    if (!field) return false;
    const candidates = [
      field.field_id,
      field.name,
      field.label,
    ].filter(Boolean);
    return candidates.some((candidate) => flaggedFieldSet.has(candidate.toLowerCase()) || flaggedFieldSet.has(formatFieldLabel(candidate).toLowerCase()));
  };

  // allowChartPrefill=false (signed/amendment notes) means empty historical
  // fields stay empty — current chart data is filled only on explicit
  // provider request elsewhere, never silently.
  const { setOriginalChartData } = useChartData(patientId, practitionerId, template, setFormData, initialData, caseId, allowChartPrefill);
  const medicationFields = React.useMemo(() => {
    const result = [];
    const collect = (fieldList) => {
      fieldList.forEach((field) => {
        if (
          ['medication_list', 'supplement_list'].includes(field.type)
          && ['patient_medications', 'patient_supplements'].includes(field.auto_fill_from)
        ) {
          result.push(field);
        }
        if (field.fields) collect(field.fields);
      });
    };
    collect(fields);
    return result;
  }, [fields]);

  // Track the last data we propagated to the parent so we can distinguish
  // parent echoes (from our own onChange) from genuine external resets.
  const lastPropagatedData = useRef(initialData);
  const hasUnsavedEdits = useRef(false);
  const medicationDirtyRef = useRef(new Set());
  const [pendingMedicationRefresh, setPendingMedicationRefresh] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const wasHighlightingMissingFields = useRef(false);
  const hasValidatedForm = useRef(false);

  useEffect(() => {
    // Once the user has made edits, don't let external parent updates (e.g.,
    // background note refetches) clobber the local form state.
    if (initialData === lastPropagatedData.current) return;
    // While a form field is focused (e.g. during a fill/typing action), don't
    // reset the local form state from an external update, because the focused
    // input's DOM value has not yet propagated through onChange and resetting
    // would race and lose the edit.
    if (typeof document !== 'undefined' && formRef.current && formRef.current.contains(document.activeElement)) return;
    setFormData(prev => {
      if (hasUnsavedEdits.current) return prev;
      lastPropagatedData.current = initialData;
      formDataRef.current = initialData;
      return initialData;
    });
  }, [initialData]);

  // Notify parent of form data changes via useEffect to avoid updating parent during render
  const [formDataChanged, setFormDataChanged] = useState(false);
  useEffect(() => {
    if (formDataChanged && onChange) {
      lastPropagatedData.current = formData;
      onChange(formData);
      setFormDataChanged(false);
    }
  }, [formData, formDataChanged, onChange]);

  const shouldShowField = useCallback((field) => {
    return shouldShowFieldUtil(field, formData);
  }, [formData]);

  const handleFieldChange = (fieldId, value) => {
    hasUnsavedEdits.current = true;
    if (medicationFields.some((field) => getFieldKey(field) === fieldId)) {
      medicationDirtyRef.current.add(fieldId);
    }
    // Support batch updates: when fieldId is null, value is an object of {fieldId: value} pairs
    if (fieldId === null && typeof value === 'object' && value !== null) {
      setFormData(prev => {
        const next = { ...prev, ...value };
        formDataRef.current = next;
        return next;
      });
      setFormDataChanged(true);
      return;
    }
    setFormData(prev => {
      const next = { ...prev, [fieldId]: value };
      formDataRef.current = next;
      return next;
    });
    setFormDataChanged(true);
  };

  const refreshMedicationFields = useCallback(async () => {
    if (!patientId || medicationFields.length === 0) return;
    const response = await get(`/medications/patients/${patientId}/medications?status=active`);
    if (!response.ok) throw new Error('Unable to load current chart medications');
    const data = await response.json();
    const medications = data.medications || [];
    const updates = {};
    let blocked = false;

    medicationFields.forEach((field) => {
      const fieldKey = getFieldKey(field);
      if (medicationDirtyRef.current.has(fieldKey)) {
        blocked = true;
        return;
      }
      const isSupplementField = field.auto_fill_from === 'patient_supplements'
        || (!field.auto_fill_from && field.type === 'supplement_list');
      const relevantMedications = isSupplementField
        ? medications.filter((medication) => medication.item_type === 'supplement')
        : medications.filter((medication) => medication.item_type !== 'supplement');
      updates[fieldKey] = field.type === 'textarea'
        ? formatMedicationsAsText(relevantMedications, { includeOutside: true })
        : transformMedicationsForForm(relevantMedications, { includeOutside: true });
    });

    if (Object.keys(updates).length > 0) {
      setFormData((prev) => {
        const next = { ...prev, ...updates };
        formDataRef.current = next;
        return next;
      });
      setOriginalChartData((prev) => ({ ...prev, ...updates }));
      setFormDataChanged(true);
    }
    setPendingMedicationRefresh(blocked);
  }, [medicationFields, patientId, setOriginalChartData]);

  const preserveMedicationDocumentation = useCallback(() => {
    setPendingMedicationRefresh(false);
  }, []);

  const reloadMedicationFromChart = useCallback(async () => {
    medicationDirtyRef.current.clear();
    setPendingMedicationRefresh(false);
    await refreshMedicationFields();
  }, [refreshMedicationFields]);

  const validateForm = () => {
    // dx is owned by the session-info surface on unsigned notes and renders
    // read-only historical content on signed notes, so the legacy dx field
    // is always exempt from required validation.
    const newErrors = validateFormUtil(template, formDataRef.current, (field) =>
      shouldShowField(field) && field.type !== 'diagnosis_autocomplete');
    hasValidatedForm.current = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (highlightMissingFields && !wasHighlightingMissingFields.current) {
      hasValidatedForm.current = true;
    }
    wasHighlightingMissingFields.current = highlightMissingFields;
    if (hasValidatedForm.current) {
      setErrors(validateFormUtil(template, formData, shouldShowField));
    }
  }, [highlightMissingFields, template, formData, shouldShowField]);

  // Save path: callers (e.g. NotePage) own save/sign orchestration and the
  // chart-update confirm flow; this form only validates and forwards the data.
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill in all required fields before saving.');
      const firstErrorEl = document.querySelector('.border-red-500');
      if (firstErrorEl) firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    try {
      if (onSubmit) {
        await onSubmit({ ...formDataRef.current });
      }
    } catch (error) {
      logger.error('Error saving note:', error);
      toast.error('Failed to save note. Please try again.');
    }
  };

  // Helper function to get the effective label for a field (supports conditional_label)
  const getEffectiveLabel = (field) => {
    if (field.conditional_label) {
      const { field_id: condFieldId, value: condValue, label: condLabel } = field.conditional_label;
      if (formData[condFieldId] === condValue) {
        return condLabel;
      }
    }
    return field.label;
  };

  const renderMedicationActions = (fieldKey: string) => (
    <div id="note-medication-actions" key={`medication-actions-${fieldKey}`} className="mt-2">
      <PrescriptionActionLauncher
        patientId={patientId || ''}
        noteId={noteId}
        canPrescribe={canPrescribe}
        onMedicationsRefreshed={refreshMedicationFields}
      />
      {pendingMedicationRefresh && (
        <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert">
          <p className="font-medium">Medication list changed — reload from chart?</p>
          <p className="mt-1">Reloading replaces unsaved medication documentation in this note.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void reloadMedicationFromChart()}
              className="min-h-[44px] rounded-md bg-amber-700 px-3 py-2 font-medium text-white hover:bg-amber-800"
            >
              Reload from chart
            </button>
            <button
              type="button"
              onClick={preserveMedicationDocumentation}
              className="min-h-[44px] rounded-md border border-amber-700 bg-white px-3 py-2 font-medium text-amber-800 hover:bg-amber-100"
            >
              Keep my documentation
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderField = (
    field,
    options: { outsideSectionMode?: 'standalone' | 'grouped' } = {},
  ) => {
    const fieldKey = getFieldKey(field);
    // Create a modified field with the effective label for conditional_label support
    const effectiveField = {
      ...field,
      label: getEffectiveLabel(field),
    };

    // Determine if this specific field is editable based on section restrictions
    const fieldReadOnly = !isFieldEditable(fieldKey);

    const commonProps = {
      field: effectiveField,
      value: formData[fieldKey] || field.default_value || '',
      onChange: (value) => handleFieldChange(fieldKey, value),
      error: errors[fieldKey],
      readOnly: fieldReadOnly,
    };

    switch (field.type) {
      case 'text':
        return <TextFieldRenderer key={fieldKey} {...commonProps} />;
      case 'textarea':
        return <TextareaRenderer key={fieldKey} {...commonProps} />;
      case 'dropdown':
      case 'dropdown_with_freetext':
        return <DropdownRenderer key={fieldKey} {...commonProps} />;
      case 'checkbox':
        return <CheckboxRenderer key={fieldKey} {...commonProps} />;
      case 'date':
        return <DateRenderer key={fieldKey} {...commonProps} />;
      case 'table':
        return <TableRenderer key={fieldKey} {...commonProps} />;
      case 'multiselect':
      case 'multiselect_with_freetext':
        return <DropdownRenderer key={fieldKey} {...commonProps} multiple />;
      case 'number':
        return <TextFieldRenderer key={fieldKey} {...commonProps} type="number" />;
      case 'treatment_objectives':
        return <ObjectiveProgressRenderer key={fieldKey} {...commonProps} fieldConfig={field} />;
      case 'diagnosis_autocomplete':
        // Encounter notes have a single canonical dx editor (Services &
        // Diagnoses); the field value is mirrored into the note's content for
        // rendering. Encounter-less notes only ever show frozen values.
        if (encounterId) return null;
        return <DiagnosisAutocomplete key={fieldKey} {...commonProps} />;
      case 'medication_autocomplete':
        return (
          <MedicationAutocomplete
            key={fieldKey}
            {...commonProps}
            patientId={patientId}
            canPrescribe={canPrescribe}
          />
        );
      case 'medication_list':
        return (
          <MedicationAutocomplete
            key={fieldKey}
            {...commonProps}
            isMedicationList={true}
            outsideSectionMode={options.outsideSectionMode}
            patientId={patientId}
            canPrescribe={canPrescribe}
          />
        );
      case 'supplement_list':
        return (
          <MedicationAutocomplete
            key={fieldKey}
            {...commonProps}
            isMedicationList={true}
            isSupplementList={true}
            outsideSectionMode={options.outsideSectionMode}
            patientId={patientId}
            canPrescribe={canPrescribe}
          />
        );
      case 'section_header':
        return (
          <div key={fieldKey} className="mt-4 mb-2 border-b border-gray-300 pb-1">
            <h3 className="text-base font-semibold text-gray-800">{field.label}</h3>
            {field.help_text && (
              <p className="text-sm text-gray-600 mt-1">{field.help_text}</p>
            )}
          </div>
        );
      case 'mse_group':
        return (
          <MSEFieldGroup
            key={fieldKey}
            fields={field.fields || []}
            formData={formData}
            onChange={handleFieldChange}
            errors={errors}
            readOnly={fieldReadOnly}
            title={field.label}
          />
        );
      case 'risk_assessment_group':
        return (
          <RiskAssessmentGroup
            key={fieldKey}
            fields={field.fields || []}
            formData={formData}
            onChange={handleFieldChange}
            errors={errors}
            readOnly={fieldReadOnly}
          />
        );
      case 'vitals_group':
        return (
          <VitalsFieldGroup
            key={fieldKey}
            formData={formData}
            onChange={handleFieldChange}
            errors={errors}
            readOnly={fieldReadOnly}
          />
        );
      case 'safety_plan_group':
        return (
          <SafetyPlanGroup
            key={fieldKey}
            fields={field.fields || []}
            formData={formData}
            onChange={handleFieldChange}
            errors={errors}
            readOnly={fieldReadOnly}
          />
        );
      case 'group': {
        const childIds = (field.fields || []).map(getFieldKey);
        // MSE groups are identified by exact child ids — a substring test
        // misroutes groups like "reorientation_status" or "general_appearance"
        // (KAP post-session assessment, behavioral observations) that are not
        // mental status exams.
        const isMSEGroup = childIds.some(id => id.startsWith('mse_') || MSE_GROUP_FIELD_IDS.has(id));
        const isRiskGroup = childIds.some(id =>
          id.includes('risk_') || id.includes('suicidal_ideation') || id.includes('homicidal_ideation'));

        if (isMSEGroup) {
          return (
            <MSEFieldGroup
              key={fieldKey}
              fields={field.fields || []}
              formData={formData}
              onChange={handleFieldChange}
              errors={errors}
              readOnly={fieldReadOnly}
              title={field.label}
            />
          );
        } else if (isRiskGroup) {
          return (
            <RiskAssessmentGroup
              key={fieldKey}
              fields={field.fields || []}
              formData={formData}
              onChange={handleFieldChange}
              errors={errors}
              readOnly={fieldReadOnly}
            />
          );
        }else {
          return (
            <div key={fieldKey} className="border border-gray-300 rounded p-3 mb-2">
              <h3 className="text-base font-semibold mb-2">{field.label}</h3>
              {field.help_text && (
                <p className="text-sm text-gray-600 mb-2">{field.help_text}</p>
              )}
              {field.fields && field.fields.map(subField => renderFieldWithAttribution(subField))}
            </div>
          );
        }
      }
      default:
        return (
          <div key={fieldKey} className="text-red-500">
            Unsupported field type: {field.type}
          </div>
        );
    }
  };

  // Wrap every field in a stable relative container so that an attribution badge
  // appearing after mount (e.g., when permissions load) does not unmount/remount
  // the input and lose in-flight user edits.
  const renderFieldWithAttribution = (
    field,
    options: { outsideSectionMode?: 'standalone' | 'grouped' } = {},
  ) => {
    const rendered = renderField(field, options);
    if (!rendered) return null;

    const fk = getFieldKey(field);
    const fieldError = errors[fk];
    // Per-field attribution takes precedence over global createdByName
    const fieldAttr = fieldAttribution?.[fk];
    const attributionName = fieldAttr?.user_name || null;

    // Show badge if: (a) per-field attribution exists, or (b) fallback to global
    // createdByName for staff_entry_allowed fields with data
    const showBadge = attributionName
      ? (formData[fk] != null &&
         formData[fk] !== '' &&
         formData[fk] !== field.default_value)
      : (createdByName &&
         field.staff_entry_allowed === true &&
         formData[fk] != null &&
         formData[fk] !== '' &&
         formData[fk] !== field.default_value);

    const displayName = attributionName || createdByName;
    const flagged = isFieldFlagged(field);

    return (
      <div
        key={`field-${fk}`}
        className={`relative ${
          fieldError
            ? 'p-2 mb-3 rounded-md bg-red-50 dark:bg-red-900/20 border-2 border-red-500'
            : flagged
              ? 'p-2 rounded bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500'
              : ''
        }`}
      >
        {showBadge && (
          <div className="absolute -top-2 right-2 z-10">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-700">
              Entered by {displayName}
            </span>
          </div>
        )}
        {flagged && (
          <div className="absolute -top-2 left-2 z-10">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-yellow-800 dark:text-yellow-200 bg-yellow-200 dark:bg-yellow-800/40 border border-yellow-300 dark:border-yellow-700">
              <AlertTriangle size={12} /> AI review flag
            </span>
          </div>
        )}
        {rendered}
        {reportedInformation?.(fk)}
        {field.type === 'medication_list' && renderMedicationActions(fk)}
      </div>
    );
  };

  const renderVisibleFields = () => {
    const renderedFields = [];
    for (let index = 0; index < fields.length; index += 1) {
      const field = fields[index];
      if (!shouldShowField(field)) continue;

      const nextField = fields[index + 1];
      if (
        field.type === 'medication_list'
        && nextField?.type === 'supplement_list'
        && shouldShowField(nextField)
      ) {
        const medicationKey = getFieldKey(field);
        const supplementKey = getFieldKey(nextField);
        renderedFields.push(
          <section
            key={`medications-and-supplements-${medicationKey}-${supplementKey}`}
            className="rounded-lg border border-gray-200 p-3 dark:border-gray-700"
          >
            <h3 className="mb-1 text-base font-semibold text-gray-900 dark:text-white">
              Medications and Supplements
            </h3>
            <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
              Current, unknown, and past chart items are grouped below by practice responsibility and outside/client-reported source.
            </p>
            {renderField(field, { outsideSectionMode: 'standalone' })}
            {renderMedicationActions(medicationKey)}
            {renderField(nextField, { outsideSectionMode: 'standalone' })}
          </section>,
        );
        index += 1;
        continue;
      }

      renderedFields.push(renderFieldWithAttribution(field, { outsideSectionMode: 'standalone' }));
    }
    return renderedFields;
  };

  return (
    <>
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-3">
        <div className="bg-white shadow-sm rounded-lg p-4">
          <GroundingReviewAlert review={groundingReview} />
          {Object.keys(errors).length > 0 && (
            <div
              role="alert"
              data-testid="required-fields-alert"
              className="mb-3 rounded-md border-2 border-red-500 bg-red-50 px-3 py-2 text-sm font-medium text-red-800 dark:border-red-400 dark:bg-red-900/30 dark:text-red-200"
            >
              Complete the highlighted required fields before signing or saving this note.
            </div>
          )}
          <h2 className="text-xl font-bold mb-2">{template.name}</h2>
          {template.description && (
            <p className="text-gray-600 mb-3">{template.description}</p>
          )}

          {renderVisibleFields()}
        </div>
      </form>
    </>
  );
};

export default DynamicForm;
